import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Calculator,
  DollarSign,
  Truck,
  Shield,
  Package,
  TrendingDown,
  Globe,
  ArrowRight,
  Loader2,
  X,
  Check,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { apiPost, ApiError } from '../lib/apiClient';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
];

// Map destination countries to their default currencies
const getDefaultCurrencyForCountry = (country) => {
  const currencyMap = {
    'United States': 'USD',
    'United Kingdom': 'GBP',
    'Germany': 'EUR',
    'France': 'EUR',
    'Italy': 'EUR',
    'Spain': 'EUR',
    'Netherlands': 'EUR',
    'Belgium': 'EUR',
    'Austria': 'EUR',
    'Ireland': 'EUR',
    'Portugal': 'EUR',
    'Finland': 'EUR',
    'Greece': 'EUR',
    'Japan': 'JPY',
    'China': 'CNY',
    'Canada': 'CAD',
    'Australia': 'AUD',
    'Mexico': 'MXN',
    'India': 'INR',
    'South Korea': 'KRW',
    'Singapore': 'SGD',
    'Switzerland': 'CHF',
  };
  return currencyMap[country] || 'USD';
};

const LandedCostCalculator = ({ 
  dutyRate = 0, 
  suggestedDutyRate,
  productCost = 0,
  originCountry = '',
  destinationCountry = 'United States',
  alternativeOrigins = null,
  trigger = null,
  onComplete = null,
  analysisId = null  // Add analysisId to save landed cost data
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Default currency based on destination country
  const defaultCurrency = getDefaultCurrencyForCountry(destinationCountry);
  
  const [formData, setFormData] = useState({
    product_cost: productCost || '',
    currency: defaultCurrency,
    destination_country: destinationCountry,
    duty_rate: suggestedDutyRate || dutyRate || 0,
    shipping_cost: '',
    insurance_cost: '',
    quantity: 1,
  });

  const handleCalculate = async () => {
    if (!formData.product_cost || formData.product_cost <= 0) {
      toast.error('Please enter a valid product cost');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        product_cost: parseFloat(formData.product_cost),
        currency: formData.currency,
        destination_country: formData.destination_country,
        duty_rate: parseFloat(formData.duty_rate),
        shipping_cost: formData.shipping_cost ? parseFloat(formData.shipping_cost) : 0,
        insurance_cost: formData.insurance_cost ? parseFloat(formData.insurance_cost) : 0,
        quantity: parseInt(formData.quantity) || 1,
        origin_country: originCountry,
      };

      // Add alternative origins for comparison if available
      if (alternativeOrigins && alternativeOrigins.length > 0) {
        payload.alternative_origins = alternativeOrigins.map(alt => ({
          country: alt.country,
          duty_rate: alt.effective_tariff || alt.duty_rate || dutyRate,
          shipping_cost: formData.shipping_cost ? parseFloat(formData.shipping_cost) : 0
        }));
      }

      const data = await apiPost('/api/landed-cost/calculate', payload);
      setResult(data);

      // Save to analysis if analysisId is provided
      if (analysisId) {
        try {
          await apiPost('/api/landed-cost/save', {
            analysis_id: analysisId,
            landed_cost_data: data,
          });
        } catch (saveError) {
          console.error('Failed to save landed cost:', saveError);
        }
      }

      // Call onComplete callback if provided
      if (onComplete) {
        onComplete(data);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message || 'Calculation failed');
      } else {
        toast.error('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, symbol = '$') => {
    return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getCurrencySymbol = () => {
    return CURRENCIES.find(c => c.code === formData.currency)?.symbol || '$';
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button 
            variant="outline" 
            className="rounded-xl border-[#059669]/30 text-[#059669] hover:bg-[#059669]/5"
            data-testid="calculate-landed-cost-btn"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Calculate Landed Cost
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-slate-200">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-slate-900">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#059669] to-[#10B981] flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            Landed Cost Estimator
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          /* Input Form */
          <div className="space-y-6 mt-4">
            {/* Required Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Product Cost per Unit *
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {getCurrencySymbol()}
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.product_cost}
                    onChange={(e) => setFormData({ ...formData, product_cost: e.target.value })}
                    className="pl-8 bg-white border-slate-200 text-slate-900"
                    data-testid="landed-cost-product-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(v) => setFormData({ ...formData, currency: v })}
                >
                  <SelectTrigger data-testid="landed-cost-currency-select" className="bg-white border-slate-200 text-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {CURRENCIES.map(c => (
                      <SelectItem key={c.code} value={c.code} className="text-slate-900">
                        {c.symbol} {c.code} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Duty Rate (pre-filled from classification) */}
            <div className="p-4 bg-[#059669]/5 rounded-xl border border-[#059669]/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#059669]" />
                  <span className="font-medium text-slate-700">Duty Rate</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.duty_rate}
                    onChange={(e) => setFormData({ ...formData, duty_rate: e.target.value })}
                    className="w-24 text-right bg-white border-slate-200 text-slate-900"
                    data-testid="landed-cost-duty-input"
                  />
                  <span className="text-slate-600">%</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Pre-filled from classification. Adjust if needed.
              </p>
            </div>

            {/* Optional Inputs */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-600">Optional Costs</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <Truck className="w-3 h-3" /> Shipping/Unit
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      {getCurrencySymbol()}
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.shipping_cost}
                      onChange={(e) => setFormData({ ...formData, shipping_cost: e.target.value })}
                      className="pl-7 text-sm bg-white border-slate-200 text-slate-900"
                      data-testid="landed-cost-shipping-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Insurance/Unit
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      {getCurrencySymbol()}
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.insurance_cost}
                      onChange={(e) => setFormData({ ...formData, insurance_cost: e.target.value })}
                      className="pl-7 text-sm bg-white border-slate-200 text-slate-900"
                      data-testid="landed-cost-insurance-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <Package className="w-3 h-3" /> Quantity
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="text-sm bg-white border-slate-200 text-slate-900"
                    data-testid="landed-cost-quantity-input"
                  />
                </div>
              </div>
            </div>

            {/* Calculate Button */}
            <Button
              onClick={handleCalculate}
              disabled={loading}
              className="w-full py-6 bg-gradient-to-r from-[#059669] to-[#10B981] text-white hover:opacity-90"
              data-testid="calculate-landed-cost-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="w-5 h-5 mr-2" />
                  Calculate Landed Cost
                </>
              )}
            </Button>
          </div>
        ) : (
          /* Results View */
          <div className="space-y-6 mt-4">
            {/* Cost Breakdown Card */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#059669]" />
                Cost Breakdown (Per Unit)
              </h3>
              
              <div className="space-y-3">
                {/* Product Cost */}
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-slate-600">Product Cost</span>
                  </div>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(result.product_cost_per_unit, result.currency_symbol)}
                  </span>
                </div>
                
                {/* Shipping */}
                {result.shipping_cost_per_unit > 0 && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span className="text-slate-600">Shipping</span>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(result.shipping_cost_per_unit, result.currency_symbol)}
                    </span>
                  </div>
                )}
                
                {/* Insurance */}
                {result.insurance_cost_per_unit > 0 && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <span className="text-slate-600">Insurance</span>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(result.insurance_cost_per_unit, result.currency_symbol)}
                    </span>
                  </div>
                )}
                
                {/* Duty */}
                <div className="flex items-center justify-between py-2 border-t border-slate-200 mt-2 pt-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-slate-600">Import Duty ({result.duty_rate_percent}%)</span>
                  </div>
                  <span className="font-semibold text-red-600">
                    {formatCurrency(result.duty_cost_per_unit, result.currency_symbol)}
                  </span>
                </div>
                
                {/* Total Landed Cost */}
                <div className="flex items-center justify-between py-3 bg-[#059669]/10 rounded-xl px-4 mt-4">
                  <span className="font-semibold text-[#059669]">Estimated Landed Cost</span>
                  <span className="text-2xl font-bold text-[#059669]">
                    {formatCurrency(result.landed_cost_per_unit, result.currency_symbol)}
                  </span>
                </div>
              </div>

              {/* Visual Breakdown Bar */}
              <div className="mt-6">
                <p className="text-xs text-slate-500 mb-2">Cost Composition</p>
                <div className="h-4 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-blue-500 transition-all" 
                    style={{ width: `${result.breakdown_percentages.product}%` }}
                    title={`Product: ${result.breakdown_percentages.product}%`}
                  ></div>
                  {result.breakdown_percentages.shipping > 0 && (
                    <div 
                      className="bg-amber-500 transition-all" 
                      style={{ width: `${result.breakdown_percentages.shipping}%` }}
                      title={`Shipping: ${result.breakdown_percentages.shipping}%`}
                    ></div>
                  )}
                  {result.breakdown_percentages.insurance > 0 && (
                    <div 
                      className="bg-purple-500 transition-all" 
                      style={{ width: `${result.breakdown_percentages.insurance}%` }}
                      title={`Insurance: ${result.breakdown_percentages.insurance}%`}
                    ></div>
                  )}
                  <div 
                    className="bg-red-500 transition-all" 
                    style={{ width: `${result.breakdown_percentages.duty}%` }}
                    title={`Duty: ${result.breakdown_percentages.duty}%`}
                  ></div>
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    Product {result.breakdown_percentages.product}%
                  </div>
                  {result.breakdown_percentages.shipping > 0 && (
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      Ship {result.breakdown_percentages.shipping}%
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    Duty {result.breakdown_percentages.duty}%
                  </div>
                </div>
              </div>
            </div>

            {/* Quantity Summary */}
            {result.quantity > 1 && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total for {result.quantity} units</p>
                    <p className="text-xs text-blue-400 mt-1">
                      Duty: {formatCurrency(result.total_duty_cost, result.currency_symbol)}
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">
                    {formatCurrency(result.total_landed_cost, result.currency_symbol)}
                  </p>
                </div>
              </div>
            )}

            {/* Origin Comparison */}
            {result.origin_comparison && result.origin_comparison.length > 0 && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
                <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-indigo-600" />
                  Landed Cost by Origin
                </h3>
                
                {/* Visual Bar Chart */}
                <div className="space-y-3 mb-6">
                  {result.origin_comparison.map((origin, idx) => {
                    const maxCost = Math.max(...result.origin_comparison.map(o => o.landed_cost));
                    const barWidth = (origin.landed_cost / maxCost) * 100;
                    const isBest = idx === 0;
                    
                    return (
                      <div key={origin.country} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            {isBest && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">
                                BEST
                              </span>
                            )}
                            <span className={`font-medium ${isBest ? 'text-green-700' : 'text-slate-600'}`}>
                              {origin.country}
                            </span>
                            <span className="text-slate-400 text-xs">({origin.duty_rate}% duty)</span>
                          </div>
                          <span className={`font-semibold ${isBest ? 'text-green-700' : 'text-slate-700'}`}>
                            {formatCurrency(origin.landed_cost, result.currency_symbol)}
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              isBest ? 'bg-gradient-to-r from-green-400 to-green-500' : 'bg-gradient-to-r from-indigo-300 to-indigo-400'
                            }`}
                            style={{ width: `${barWidth}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Comparison Table */}
                <div className="bg-white/60 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-indigo-100">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Origin</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Duty</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Landed Cost</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">vs Current</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.origin_comparison.map((origin, idx) => (
                        <tr key={origin.country} className={idx === 0 ? 'bg-green-50' : ''}>
                          <td className="py-2 px-3 font-medium">
                            {idx === 0 && <Check className="w-4 h-4 text-green-600 inline mr-1" />}
                            {origin.country}
                          </td>
                          <td className="py-2 px-3 text-right text-slate-600">{origin.duty_rate}%</td>
                          <td className="py-2 px-3 text-right font-semibold">
                            {formatCurrency(origin.landed_cost, result.currency_symbol)}
                          </td>
                          <td className={`py-2 px-3 text-right font-semibold ${
                            origin.difference_from_current < 0 ? 'text-green-600' : 
                            origin.difference_from_current > 0 ? 'text-red-600' : 'text-slate-400'
                          }`}>
                            {origin.difference_from_current === 0 ? '—' : 
                              origin.difference_from_current > 0 ? 
                                `+${formatCurrency(origin.difference_from_current, result.currency_symbol)}` :
                                formatCurrency(origin.difference_from_current, result.currency_symbol)
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {result.origin_comparison[0]?.savings_percent > 0 && (
                  <div className="mt-4 flex items-center gap-2 text-green-700 bg-green-100 rounded-lg px-4 py-2">
                    <TrendingDown className="w-5 h-5" />
                    <span className="font-medium">
                      Potential savings of {result.origin_comparison[0].savings_percent}% by sourcing from {result.origin_comparison[0].country}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setResult(null)}
                className="flex-1 bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                Recalculate
              </Button>
              <Button
                onClick={() => setOpen(false)}
                className="flex-1 bg-[#059669] hover:bg-[#047857] text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LandedCostCalculator;
